/**
 * Universal Login Page
 * Page de connexion unique pour TOUS les types d'utilisateurs
 * - super_admin, admin, manager → /admin/erp
 * - employee → /employee/dashboard
 * - client → /client/dashboard
 */

import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUniversalAuth } from "../../contexts/UniversalAuthContext";
import { getDefaultRouteByRole } from "../../utils/authRedirect";
import "../../styles/auth/UniversalAuth.css";

export default function UniversalLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, authLoading } = useUniversalAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from?.pathname;
  const successMessage = location.state?.message;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const user = await login(formData);
      const targetRoute = from || getDefaultRouteByRole(user.role);
      navigate(targetRoute, { replace: true });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Connexion impossible. Vérifiez vos informations.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="universal-auth-container">
      <div className="auth-card">
        {/* Header avec logo */}
        <div className="auth-header">
          <div className="auth-logo">
            <div className="logo-icon">🌲</div>
            <h1>Naturellement Vert</h1>
            <p>Arborescence ERP</p>
          </div>
        </div>

        <div className="auth-content">
          <h2>Connexion</h2>
          <p className="auth-subtitle">Connectez-vous à votre espace</p>

          {successMessage && (
            <div className="alert alert-success">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Adresse email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
                placeholder="votre@email.com"
                data-testid="login-email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="current-password"
                required
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary btn-full"
              data-testid="login-submit"
            >
              {submitting ? "Connexion en cours..." : "Se connecter"}
            </button>
          </form>

          <div className="auth-footer">
            <Link to="/auth/forgot-password" className="link-secondary">
              Mot de passe oublié ?
            </Link>
          </div>
        </div>
      </div>

      <div className="auth-legal">
        <p>© 2026 Naturellement Vert Fertilisation. Tous droits réservés.</p>
      </div>
    </div>
  );
}
